import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, shareReplay, of, delay, map, pipe, timer, switchMap, filter, throwError } from 'rxjs';
import { utils } from '../helper/utils';
import { TotalCost } from '../models/total-cost-model';
import { MaintenanceOrderHeader } from '../models/maintenance-order-header.model';
import { Equipment } from '../models/equipment.model';
import { EquipmentRemark } from '../models/equipment-remark.model';
import { MaintenanceOrderRemark } from '../models/maintenance-order-remark.model';
import { MaintenanceOrderFilter } from '../models/maintenance-order-filter.model';
import { MaintenanceOrderResponse } from '../models/maintenance-order-response.model';
import { MaintenanceOrderCreateRequest } from '../models/maintenance-order-create-request.model';
import { EMPTY_SUBSCRIPTION } from 'rxjs/internal/Subscription';
import { MaintenanceOrderChangeResult } from '../models/maintenance-order-change-result.model';
import { Currency } from '../models/currency.model';
import { ValidationSummary } from '../models/validation-summary.model';
import { ValidationReferenceResponse } from '../models/validation-reference-response-model';
import { MaintenanceOrderPartSerialNumberUpdateRequest } from '../models/maintenance-order-part-serial-number-update-request.model';
import { EquipmentNumberValidationModel } from '../models/equipment-number-validation.model';
import { actionButtonRequestModel, actionButtonResponse, manualApprovalFilter } from '../models/manual-approval-filter.model';
import { AuditLog } from '../models/audit-log.model';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderService {
  constructor(private httpClient: HttpClient) {}

  validateEquipmentNumber(
    equipmentNumber: string | undefined,
    maintenanceRepairModeCode: string | undefined,
  ): Observable<EquipmentNumberValidationModel> {
    return this.httpClient.post<EquipmentNumberValidationModel>(
      utils.GetOrderAPIUrl('maintenance-orders/validate?property=equipmentPastRepairNumber'),
      {
        equipmentNumber: equipmentNumber,
        maintenanceRepairModeCode: maintenanceRepairModeCode,
      },
    );
  }
  calculateTotalCost(workorderId: string | undefined, maintenanceOrder: MaintenanceOrderCreateRequest): Observable<TotalCost> {
    if (workorderId) {
      return this.httpClient.post<TotalCost>(
        `${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/calculate-cost`,
        maintenanceOrder,
      );
    }
    return this.httpClient.post<TotalCost>(utils.GetOrderAPIUrl('maintenance-orders/calculate-cost'), maintenanceOrder);
  }
  createOrUpdateWorkOrder(
    maintenanceOrder: MaintenanceOrderCreateRequest,
    workorderId: string | undefined = undefined,
  ): Observable<MaintenanceOrderResponse> {
    if (workorderId) {
      return this.httpClient
        .put<void>(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/aems-ui`, maintenanceOrder, { observe: 'response' })
        .pipe(
          switchMap((response) => {
            if (response.status === 200) {
              return this.getWorkOrderById(workorderId);
            } else {
              return throwError(() => response);
            }
          }),
          catchError((error) => {
            return throwError(() => error);
          }),
        );
    }
    return this.httpClient.post<MaintenanceOrderResponse>(utils.GetOrderAPIUrl('maintenance-orders/aems-ui'), maintenanceOrder);
  }
  saveOrUpdateDraftWorkOrder(
    maintenanceOrder: MaintenanceOrderCreateRequest,
    workOrderId: string | undefined = undefined,
  ): Observable<MaintenanceOrderResponse> {
    if (workOrderId) {
      return this.httpClient
        .put<void>(`${utils.GetOrderAPIUrl('maintenance-orders/draft')}/${workOrderId}`, maintenanceOrder, { observe: 'response' })
        .pipe(
          switchMap((response) => {
            if (response.status === 200) {
              return this.getWorkOrderById(workOrderId);
            } else {
              return throwError(() => response);
            }
          }),
          catchError((error) => {
            return throwError(() => error);
          }),
        );
    }
    return this.httpClient.post<MaintenanceOrderResponse>(utils.GetOrderAPIUrl('maintenance-orders/draft'), maintenanceOrder);
  }

  deleteWorkOrder(workorderId: string | undefined = undefined): Observable<any> {
    if (workorderId) {
      return this.httpClient.delete<any>(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}`);
    } else {
      return timer(2000);
    }
  }
  deleteDraftWorkOrder(workorderId: string | undefined = undefined): Observable<any> {
    if (workorderId) {
      return this.httpClient.delete<any>(`${utils.GetOrderAPIUrl('maintenance-orders/draft')}/${workorderId}`);
    } else {
      return timer(2000);
    }
  }
  approveWorkOrder(approveRequest: actionButtonRequestModel) {
    return this.httpClient.post<actionButtonResponse>(utils.GetUrlConfig('maintenance-orders/workflow-process/approve'), approveRequest);
  }
  rejectWorkOrder(rejectRequest: actionButtonRequestModel) {
    return this.httpClient.post<actionButtonResponse>(utils.GetUrlConfig('maintenance-orders/workflow-process/reject'), rejectRequest);
  }
  rejectWorkOrderTTL(rejectRequest: actionButtonRequestModel) {
    return this.httpClient.post<actionButtonResponse>(utils.GetUrlConfig('maintenance-orders/workflow-process/forward'), rejectRequest);
  }
  forwardWorkOrder(forwardRequest: actionButtonRequestModel) {
    return this.httpClient.post<actionButtonResponse>(utils.GetUrlConfig('maintenance-orders/workflow-process/forward'), forwardRequest);
  }
  totalLossWorkOrder(totalLossRequest: actionButtonRequestModel) {
    return this.httpClient.post<actionButtonResponse>(
      utils.GetUrlConfig('maintenance-orders/workflow-process/totalloss'),
      totalLossRequest,
    );
  }
  updateWorkOrder(updateRequest: any) {
    if (updateRequest) {
      return this.httpClient.post<any>(utils.GetOrderAPIUrl('maintenance-orders/update-workorder-tpicode'), updateRequest);
    } else {
      return timer(2000);
    }
  }

  getAuditLogs(workorderId: string): Observable<MaintenanceOrderChangeResult> {
    return this.httpClient.get<MaintenanceOrderChangeResult>(utils.GetOrderAPIUrl(`maintenance-orders/${workorderId}/audit-logs`));
  }

  getAuditLogsFromNewAPI(workorderId: number, isSystemLog: boolean, page: number, pageLimit: number): Observable<HttpResponse<AuditLog[]>> {
    return this.httpClient
      .get<
        AuditLog[]
      >(utils.GetOrderAPIUrl(`maintenance-orders/${workorderId}/workorder-audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${pageLimit}`), { observe: 'response' })
      .pipe(
        map((response: HttpResponse<AuditLog[]>) => {
          const body = response.body;
          if (body) {
            const mappedBody = body.map((item) => ({
              ...item,
            }));

            return new HttpResponse({
              body: mappedBody,
              headers: response.headers,
            });
          } else {
            return response;
          }
        }),
      );
  }

  completeWorkOrder(
    workorderId: string,
    maintainceOrderUpdateAndComplete: MaintenanceOrderPartSerialNumberUpdateRequest | undefined,
  ): Observable<any> {
    return this.httpClient
      .patch<void>(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/complete-repair`, maintainceOrderUpdateAndComplete, {
        observe: 'response',
      })
      .pipe(
        switchMap((response) => {
          if (response.status === 200) {
            return this.getWorkOrderById(workorderId);
          } else {
            return throwError(() => response);
          }
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }
  markAsTTl(workorderId: string): Observable<any> {
    return this.httpClient.patch(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/mark-as-ttl`, null);
  }

  rejectAsTTL(workorderId: string): Observable<any> {
    return this.httpClient.patch(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/reject-as-ttl`, null);
  }

  getWorkOrders(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: MaintenanceOrderFilter,
  ): Observable<HttpResponse<MaintenanceOrderHeader[]>> {
    let url: string = `maintenance-orders/multi-filter?page=${page}&limit=${pageLimit}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    let clonedFilters = structuredClone(filters);
    if (clonedFilters?.fromDate) {
      clonedFilters.fromDate = utils.formatToISODateUTC(clonedFilters.fromDate, true);
    }
    if (clonedFilters?.toDate) {
      clonedFilters.toDate = utils.formatToISODateUTC(clonedFilters.toDate, false);
    }
    return this.httpClient.post<MaintenanceOrderHeader[]>(utils.GetOrderAPIUrl(url), clonedFilters, { observe: 'response' }).pipe(
      map((response: HttpResponse<MaintenanceOrderHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.maintenanceOrderStatusCode).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
      catchError(() => {
        return of(new HttpResponse<MaintenanceOrderHeader[]>({ body: [], headers: undefined }));
      }),
    );
  }
  getWorkOrderById(id: string): Observable<MaintenanceOrderResponse> {
    return this.httpClient.get<MaintenanceOrderResponse>(utils.GetOrderAPIUrl(`maintenance-orders/${id}/aems-ui`));
  }

  getWorkOrderOrderExchangeRateInfoById(id: string): Observable<Currency> {
    return this.httpClient.get<Currency>(utils.GetOrderAPIUrl(`maintenance-orders/${id}/exchange-rate`));
  }

  getEquipmentDetails(workOrderId: string): Observable<Equipment | null> {
    return this.httpClient.get<Equipment>(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/equipment-details`));
  }

  getGensetHours(workOrderId: string): Observable<number | null> {
    return this.httpClient.get<number>(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/genset-hours`));
  }

  getSummary(workOrderId: string): Observable<ValidationSummary[]> {
    return this.httpClient.get<ValidationSummary[]>(utils.GetUrlConfig(`maintenance-orders/validation-summary/${workOrderId}`));
  }
  getTotalCost(workOrderId: string): Observable<TotalCost> {
    return this.httpClient.get<TotalCost>(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/saved-cost`));
  }
  validateRepairCode(maintainceOrder: MaintenanceOrderCreateRequest): Observable<any> {
    return this.httpClient.post(utils.GetOrderAPIUrl('maintenance-orders/validate?property=maintenanceTypeCode'), maintainceOrder);
  }
  getRemarks(workOrderId: string): Observable<MaintenanceOrderRemark[]> {
    return this.httpClient.get<MaintenanceOrderRemark[]>(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/remarks`));
  }
  postRemarks(workOrderId: string, remarks: MaintenanceOrderRemark[]): Observable<any> {
    return this.httpClient.post(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/remarks`), remarks);
  }
  postRemarks1(workOrderId: string, remarks: any[]): Observable<any> {
    return this.httpClient.post(utils.GetWorkFlowProcessUrl(`maintenance-orders/workflow-process/remarks`), remarks);
  }
  getValidationRefData(workOrderId: string): Observable<ValidationReferenceResponse> {
    return this.httpClient.get<ValidationReferenceResponse>(
      utils.GetUrlConfig(`maintenance-orders/validation-referencedata/${workOrderId}`),
    );
  }
}
